import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  RiMailLine, 
  RiPhoneLine, 
  RiMapPinLine, 
  RiSendPlaneFill,
  RiArrowRightLine,
  RiCheckboxCircleLine,
  RiTimeLine,
  RiGlobalLine,
  RiTwitterXLine,
  RiLinkedinLine,
  RiInstagramLine,
  RiFacebookLine,
  RiBuildingLine,
  RiCustomerService2Line,
  RiCheckLine,
  RiStarFill,
  RiTeamLine,
  RiCalendarEventLine,
  RiShieldCheckLine,
  RiHeartLine,
  RiInformationLine,
  RiPresentationLine,
  RiUserLine,
} from 'react-icons/ri';
import toast from 'react-hot-toast';
import { API_BASE } from '../config';
import { useSummit } from '../pages/summitdetails';

/* ─── THEME COLORS ─────────────────────────────────────────────────────────────── */
const T = {
  white: "#FFFFFF",
  offWhite: "#F4F9F8",
  softTeal: "#E8F5F3",
  primary: "#2A9D8F",
  primaryDark: "#1F7A6E",
  primaryLight: "#3BBCAE",
  navy: "#1A3A4A",
  navyDeep: "#0D2B3E",
  accent: "#3BBCAE",
  accentGold: "#D4A017",
  accentGoldDark: "#B8880F",
  accentGoldLight: "#E8B520",
  accentTealLight: "#6BC2B5",
  accentTealDark: "#218577",
  text: "#1C3461",
  textMid: "#2D4A5A",
  textMuted: "#6B8090",
  textLight: "#9BB0C0",
  border: "#C8E8E4",
  borderLight: "#E2F2F0",
  shadow: "0 4px 24px rgba(42,157,143,0.10)",
  shadowMd: "0 8px 40px rgba(42,157,143,0.14)",
  shadowLg: "0 20px 60px rgba(13,43,62,0.20)",
  shadowGold: "0 8px 32px rgba(212,160,23,0.25)",
  gradPrimary: "linear-gradient(135deg, #2A9D8F 0%, #3BBCAE 100%)",
  gradNavy: "linear-gradient(135deg, #0D2B3E 0%, #1A3A4A 100%)",
  gradSoft: "linear-gradient(135deg, #E8F5F3 0%, #F4F9F8 100%)",
  gradGold: "linear-gradient(135deg, #D4A017 0%, #E8B520 100%)",
  gradGoldReverse: "linear-gradient(135deg, #E8B520 0%, #D4A017 100%)",
  gradTealDark: "linear-gradient(135deg, #1F7A6E 0%, #2A9D8F 100%)",
  gradAccent: "linear-gradient(135deg, #3BBCAE 0%, #2A9D8F 100%)",
  gradSuccess: "linear-gradient(135deg, #2A9D8F 0%, #6BC2B5 100%)",
  success: "#2A9D8F",
  warning: "#D4A017",
  danger: "#218577",
};

/* ─── CSS ──────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html{scroll-behavior:smooth;-webkit-font-smoothing:antialiased}
  body,#root{background:${T.offWhite};color:${T.text};font-family:'Inter',sans-serif}
  ::-webkit-scrollbar{width:8px}
  ::-webkit-scrollbar-track{background:${T.borderLight}}
  ::-webkit-scrollbar-thumb{background:${T.primaryLight};border-radius:4px}

  .t-hero{background:${T.gradSoft};border-bottom:1px solid ${T.border};padding:clamp(48px,7vw,80px) clamp(20px,4vw,64px) clamp(40px,5vw,64px);position:relative;overflow:hidden}
  .t-hero::before{content:'';position:absolute;top:-120px;left:50%;transform:translateX(-50%);width:600px;height:600px;background:radial-gradient(circle,rgba(42,157,143,0.08) 0%,transparent 70%);pointer-events:none}
  .t-hero-i{max-width:1320px;margin:0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:40px;flex-wrap:wrap;position:relative}
  .t-hero-lbl{display:inline-flex;align-items:center;gap:8px;padding:5px 14px;border-radius:20px;background:${T.softTeal};border:1px solid ${T.border};color:${T.primary};font-size:11px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;margin-bottom:20px}
  .t-hero-h{font-family:'Playfair Display',serif;font-size:clamp(2rem,4.5vw,3.4rem);font-weight:900;color:${T.navy};letter-spacing:-.03em;line-height:1.06;margin-bottom:14px}
  .t-hero-h em{font-style:normal;color:${T.accentGold}}
  .t-hero-p{font-size:clamp(.88rem,1.3vw,1rem);color:${T.textMid};line-height:1.7;max-width:500px}
  .t-stats{display:flex;gap:1px;background:${T.border};border-radius:14px;border:1px solid ${T.border};overflow:hidden;align-self:flex-start;flex-shrink:0}
  .t-st{background:${T.white};padding:20px 28px;display:flex;flex-direction:column;gap:4px;min-width:100px}
  .t-st:nth-child(2){background:${T.offWhite}}
  .t-st-n{font-family:'Playfair Display',serif;font-size:1.8rem;font-weight:900;color:${T.accentGold};line-height:1;letter-spacing:-.03em}
  .t-st-l{font-size:11px;color:${T.textMuted};font-weight:500;text-transform:uppercase;letter-spacing:.08em}

  .t-card{background:${T.white};border-radius:20px;border:1px solid ${T.borderLight};padding:28px;box-shadow:${T.shadow};transition:all 0.3s ease}
  .t-card:hover{transform:translateY(-6px);box-shadow:${T.shadowLg}}

  .t-form-input{width:100%;padding:10px 14px;background:${T.offWhite};border:1px solid ${T.borderLight};border-radius:12px;font-size:13px;color:${T.text};transition:all 0.2s;outline:none}
  .t-form-input:focus{border-color:${T.primary};background:${T.white}}
  .t-form-label{display:block;font-size:11px;font-weight:600;color:${T.textMid};margin-bottom:4px}
  .t-required{color:#EF4444}

  .t-btn-primary{display:inline-flex;align-items:center;gap:8px;padding:10px 24px;background:${T.gradGold};color:${T.navyDeep};border:none;border-radius:40px;font-size:13px;font-weight:700;cursor:pointer;transition:all 0.2s}
  .t-btn-primary:hover:not(:disabled){transform:translateY(-2px);box-shadow:${T.shadowGold}}

  .t-cta-w{padding:0 clamp(20px,4vw,64px) 48px;background:${T.offWhite}}
  .t-cta{max-width:1320px;margin:0 auto;background:${T.gradNavy};border:1px solid rgba(59,188,174,0.28);border-radius:20px;padding:clamp(32px,4vw,52px) clamp(32px,4vw,60px);display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;position:relative;overflow:hidden}
  .t-cta::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at 50% 100%,rgba(212,160,23,0.12) 0%,transparent 60%);pointer-events:none}
  .t-cta-tag{display:inline-block;padding:4px 12px;border-radius:20px;background:rgba(212,160,23,0.15);border:1px solid rgba(212,160,23,0.25);color:${T.accentGoldLight};font-size:11px;letter-spacing:.08em;text-transform:uppercase;margin-bottom:12px;font-weight:700}
  .t-cta-h{font-family:'Playfair Display',serif;font-size:clamp(1.4rem,2.5vw,2rem);font-weight:900;color:${T.white};letter-spacing:-.025em;line-height:1.15}
  .t-cta-p{font-size:14px;color:${T.textMuted};margin-top:8px;line-height:1.6}
  .t-cta-buttons{display:flex;gap:16px;align-items:center;flex-wrap:wrap}
  .t-cta-btn{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:${T.gradGold};color:${T.navyDeep};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn:hover{background:${T.gradGoldReverse};transform:translateY(-2px);box-shadow:${T.shadowGold}}
  .t-cta-btn-outline{display:inline-flex;align-items:center;gap:8px;padding:12px 28px;background:transparent;border:1px solid rgba(255,255,255,0.3);color:${T.white};border-radius:50px;text-decoration:none;font-family:'Inter',sans-serif;font-size:14px;font-weight:700;transition:all .2s}
  .t-cta-btn-outline:hover{background:rgba(255,255,255,0.1);transform:translateY(-2px)}

  @media (max-width: 768px) {
    .t-cta-buttons { width: 100%; justify-content: center; }
    .t-cta-btn, .t-cta-btn-outline { width: 100%; justify-content: center; }
  }
`;

// ─── Default contact info structure ──────────────────────────────────────────
const defaultContactInfo = [
  {
    icon: RiMailLine,
    title: 'Email Us',
    info: 'info@meetings21.com',
    subInfo: 'We respond within 24 hours',
    action: 'mailto:info@meetings21.com',
    color: T.primary,
    gradient: T.gradPrimary,
  },
  {
    icon: RiPhoneLine,
    title: 'Call Us',
    info: '+1 (234) 567-8900',
    subInfo: 'Available Monday – Friday',
    action: 'tel:+12345678900',
    color: T.accent,
    gradient: T.gradAccent,
  },
  {
    icon: RiMapPinLine,
    title: 'Visit Us',
    info: 'Manjeera Trinity Corporate',
    subInfo: 'Hyderabad, Telangana, India',
    action: '#',
    color: T.accentGold,
    gradient: T.gradGold,
  },
];

const socialLinks = [
  { icon: RiTwitterXLine,  name: 'Twitter',   url: 'https://twitter.com',   color: '#000000' },
  { icon: RiLinkedinLine, name: 'LinkedIn',  url: 'https://linkedin.com',  color: '#0077B5' },
  { icon: RiInstagramLine,name: 'Instagram', url: 'https://instagram.com', color: '#E4405F' },
  { icon: RiFacebookLine, name: 'Facebook',  url: 'https://facebook.com',  color: '#1877F2' },
];

const stats = [
  { value: '24h',  label: 'Response Time',   icon: RiTimeLine },
  { value: '60+',  label: 'Countries Served', icon: RiGlobalLine },
  { value: '15K+', label: 'Scholars Network', icon: RiTeamLine },
  { value: '10+',  label: 'Years Experience', icon: RiStarFill },
];

const faqs = [
  { q: 'How quickly will I hear back?', a: 'Our team responds to all inquiries within 24 hours on business days.' },
  { q: 'Can I submit an abstract via email?', a: 'Abstracts should be submitted through the dedicated abstract submission portal for proper review.' },
  { q: 'Do you offer virtual attendance?', a: 'Yes, all our conferences support both in-person and remote participation.' },
  { q: 'How do I become a sponsor?', a: 'Please use the contact form and select "Sponsorship Inquiry" as the subject.' },
];

function ContactCard({ item, index }) {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <a href={item.action} style={{ textDecoration: 'none', display: 'block' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="t-card" style={{
          textAlign: 'center',
          transform: hovered ? 'translateY(-8px)' : 'translateY(0)',
          borderColor: hovered ? `${item.color}40` : T.borderLight,
        }}>
          <div style={{
            width: 72, height: 72, borderRadius: 22,
            background: item.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <item.icon size={34} color={T.white} />
          </div>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 10 }}>{item.title}</h3>
          <p style={{ fontSize: 16, fontWeight: 600, color: T.primary, marginBottom: 6 }}>{item.info}</p>
          <p style={{ fontSize: 13, color: T.textMuted }}>{item.subInfo}</p>
        </div>
      </a>
    </motion.div>
  );
}

function StatCard({ stat, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 + index * 0.08 }}
      className="t-card" style={{ textAlign: 'center', padding: '24px 16px', flex: 1, minWidth: 130 }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 14,
        background: T.gradPrimary,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 12px',
      }}>
        <stat.icon size={22} color={T.white} />
      </div>
      <div style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: 28, fontWeight: 900,
        background: T.gradPrimary,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        marginBottom: 4,
      }}>{stat.value}</div>
      <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>{stat.label}</div>
    </motion.div>
  );
}

export default function globalcontact() {
  // ✅ Get eventId from Summit context OR URL query param
  let eventId = null;
  const [searchParams] = useSearchParams();
  const urlEventId = searchParams.get('eventId');
  
  try {
    const summitContext = useSummit();
    eventId = summitContext?.eventId || urlEventId || null;
  } catch (error) {
    console.log('Contact component rendered outside Summit context, using URL param');
    eventId = urlEventId || null;
  }

  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [contactInfo, setContactInfo] = useState(defaultContactInfo);
  const [fetchingContact, setFetchingContact] = useState(true);

  // ✅ Fetch contact info when eventId changes
  useEffect(() => {
    fetchContactInfo();
  }, [eventId]);

  const fetchContactInfo = async () => {
    try {
      setFetchingContact(true);
      const url = eventId 
        ? `${API_BASE}/api/contacts/public/info?eventId=${eventId}` 
        : `${API_BASE}/api/contacts/public/info`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success && data.data && data.data.length > 0) {
        const contact = data.data[0];
        setContactInfo(prev => {
          const updated = [...prev];
          if (contact.email) {
            const emailIndex = updated.findIndex(item => item.title === 'Email Us');
            if (emailIndex !== -1) {
              updated[emailIndex].info = contact.email;
              updated[emailIndex].action = `mailto:${contact.email}`;
            }
          }
          if (contact.phone) {
            const phoneIndex = updated.findIndex(item => item.title === 'Call Us');
            if (phoneIndex !== -1) {
              updated[phoneIndex].info = contact.phone;
              updated[phoneIndex].action = `tel:${contact.phone.replace(/\s/g, '')}`;
            }
          }
          if (contact.name && updated.length > 0) {
            updated[0].subInfo = `${contact.name} will respond within 24 hours`;
          }
          return updated;
        });
        console.log('✅ Contact info loaded for event:', eventId, contact);
      } else {
        console.log('ℹ️ No contact info found for this event, using defaults');
      }
    } catch (error) {
      console.error('❌ Error fetching contact info:', error);
    } finally {
      setFetchingContact(false);
    }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('All fields are required');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        eventId: eventId || null,
      };
      
      const response = await fetch(`${API_BASE}/api/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        toast.success("Message sent! We'll get back to you soon.");
        setFormData({ name: '', email: '', subject: '', message: '' });
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        toast.error(data.message || 'Failed to send message');
      }
    } catch (err) {
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const EventIdBanner = () => {
    if (!eventId) return null;
    return (
      <div style={{
        maxWidth: 1320,
        margin: '0 auto',
        padding: '0 clamp(20px,4vw,64px)',
        marginTop: '16px',
      }} />
    );
  };

  return (
    <div style={{ background: T.offWhite, minHeight: "100vh" }}>
      <style>{css}</style>

      {/* ══════════ HERO ══════════ */}
      <section className="t-hero">
        <div className="t-hero-i">
          <div>
            <div className="t-hero-lbl">
              <RiCustomerService2Line size={13} />
              Meetings21 · Get in Touch
            </div>
            <h1 className="t-hero-h">
              Let's <em>Connect</em>
            </h1>
            <p className="t-hero-p">
              Have questions about the summit? Our team is ready to assist with any inquiries — 
              from registration to sponsorship and everything in between.
            </p>
          </div>
          <div className="t-stats">
            <div className="t-st">
              <span className="t-st-n">24h</span>
              <span className="t-st-l">Response</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">60+</span>
              <span className="t-st-l">Countries</span>
            </div>
            <div className="t-st">
              <span className="t-st-n">10+</span>
              <span className="t-st-l">Years</span>
            </div>
          </div>
        </div>
      </section>

      <EventIdBanner />

      {/* Stats Cards Row */}
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(20px,4vw,64px) 40px', display: 'flex', gap: 20, flexWrap: 'wrap', justifyContent: 'center' }}>
        {stats.map((s, i) => <StatCard key={i} stat={s} index={i} />)}
      </div>

      {/* Contact Cards */}
      <section style={{ padding: '0 clamp(20px,4vw,64px) 60px' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 28 }}>
            {contactInfo.map((item, i) => <ContactCard key={i} item={item} index={i} />)}
          </div>
        </div>
      </section>

      {/* Form + Sidebar Section */}
      <section style={{ padding: '0 clamp(20px,4vw,64px) 80px' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 40 }}>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="t-card"
            style={{ padding: '28px' }}
          >
            <div style={{ marginBottom: 20 }}>
              <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 6 }}>Send us a Message</h2>
              <p style={{ color: T.textMuted, fontSize: 12 }}>Fill out the form and we'll get back to you within 24 hours.</p>
            </div>

            {submitted && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{ background: `${T.success}10`, border: `1px solid ${T.success}30`, borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}
              >
                <RiCheckboxCircleLine size={16} color={T.success} />
                <div>
                  <p style={{ fontWeight: 700, color: T.success, marginBottom: 1, fontSize: 12 }}>Message sent successfully!</p>
                  <p style={{ fontSize: 10, color: T.textMuted }}>We'll be in touch within 24 hours.</p>
                </div>
              </motion.div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label className="t-form-label">Full Name <span className="t-required">*</span></label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Dr. Jane Smith" required className="t-form-input" />
                </div>
                <div>
                  <label className="t-form-label">Email Address <span className="t-required">*</span></label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="jane@university.edu" required className="t-form-input" />
                </div>
              </div>

              <div>
                <label className="t-form-label">Subject <span className="t-required">*</span></label>
                <select name="subject" value={formData.subject} onChange={handleChange} required className="t-form-input" style={{ cursor: 'pointer' }}>
                  <option value="">Select a subject…</option>
                  {['General Inquiry', 'Registration Help', 'Abstract Submission', 'Sponsorship Inquiry', 'Speaker Application', 'Technical Support', 'Other'].map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="t-form-label">Message <span className="t-required">*</span></label>
                <textarea name="message" value={formData.message} onChange={handleChange} rows={3} placeholder="Tell us more about your inquiry…" required className="t-form-input" style={{ resize: 'vertical' }} />
              </div>

              {eventId && (
                <div style={{ 
                  fontSize: 11, 
                  color: T.textMuted, 
                  background: T.offWhite,
                  padding: '8px 12px',
                  borderRadius: 6,
                  border: `1px solid ${T.borderLight}`
                }}>
                  📌 Message will be associated with Event ID: <strong>{eventId}</strong>
                </div>
              )}

              <button type="submit" disabled={loading} className="t-btn-primary" style={{ justifyContent: 'center', width: '100%' }}>
                {loading ? 'Sending…' : <><RiSendPlaneFill size={14} /> Send Message</>}
              </button>
            </form>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            style={{ display: 'flex', flexDirection: 'column', gap: 24 }}
          >
            {/* Support Box */}
            <div className="t-card" style={{ background: T.gradPrimary, color: T.white, padding: '24px' }}>
              <RiHeartLine size={24} color="rgba(255,255,255,0.9)" style={{ marginBottom: 10 }} />
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>We're Here to Help</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {['Dedicated support team', 'Fast 24-hour response', 'Multilingual assistance', 'Technical & logistical help'].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <RiCheckLine size={13} color="rgba(255,255,255,0.85)" />
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>{item}</span>
                  </div>
                ))}
              </div>
              <div style={{ height: 1, background: 'rgba(255,255,255,0.15)', margin: '16px 0' }} />
              <div>
                <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 12 }}>Follow Us</h4>
                <div style={{ display: 'flex', gap: 12 }}>
                  {socialLinks.map((s, i) => (
                    <motion.a key={i} href={s.url} target="_blank" rel="noopener noreferrer" whileHover={{ y: -3 }} style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none' }}>
                      <s.icon size={18} style={{ color: 'rgba(255,255,255,0.9)' }} />
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="t-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '16px 24px', borderBottom: `1px solid ${T.borderLight}` }}>
                <h3 style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>Open in Maps</h3>
                <p style={{ fontSize: 12, color: T.textMuted }}>Manjeera Trinity Corporate, Hyderabad</p>
              </div>
              <div style={{ height: 180 }}>
                <iframe 
                  title="Conference Location Map" 
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3805.7067856459695!2d78.3877827!3d17.4658107!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb915b8c1e3df1%3A0xc2f6a0e9a6c87b6d!2sManjeera%20Trinity%20Corporate!5e0!3m2!1sen!2sin!4v1699999999999" 
                  style={{ width: '100%', height: '100%', border: 0 }} 
                  allowFullScreen 
                  loading="lazy" 
                />
              </div>
              <div style={{ padding: '12px 24px', background: T.offWhite, borderTop: `1px solid ${T.borderLight}` }}>
                <p style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.5 }}>
                  <strong>Manjeera Trinity Corporate</strong><br />
                  First floor, Unit: 114, JNTU - Hitech Road, KPHB Colony,<br />
                  Kukatpally, Hyderabad - 500072, Telangana.
                </p>
              </div>
            </div>

            {/* Office Hours */}
            <div className="t-card" style={{ padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: `${T.primary}15`, border: `1px solid ${T.primary}25`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <RiTimeLine size={22} color={T.primary} />
                </div>
                <h3 style={{ fontSize: 20, fontWeight: 800, color: T.text }}>Office Hours</h3>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid }}>Monday – Friday</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.primary }}>9:00 AM – 6:00 PM EST</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: `1px solid ${T.borderLight}` }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid }}>Saturday</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.primary }}>10:00 AM – 2:00 PM EST</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0' }}>
                  <span style={{ fontSize: 13, fontWeight: 500, color: T.textMid }}>Sunday</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: T.danger }}>Closed</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '60px clamp(20px,4vw,64px) 80px', background: T.softTeal }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <div className="t-hero-lbl" style={{ display: 'inline-flex' }}>FAQ</div>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 36, fontWeight: 800, color: T.text, marginTop: 8 }}>Frequently Asked <span style={{ color: T.primary }}>Questions</span></h2>
            <div style={{ width: 80, height: 3, background: T.gradPrimary, borderRadius: 2, margin: '16px auto 0' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {faqs.map((faq, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                style={{ background: T.white, borderRadius: 20, border: `1px solid ${openFaq === i ? T.primary + '40' : T.borderLight}`, overflow: 'hidden', boxShadow: T.shadow }}
              >
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} style={{ width: '100%', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{faq.q}</span>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: openFaq === i ? T.gradGold : T.softTeal, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: openFaq === i ? T.navyDeep : T.primary, fontSize: 18, lineHeight: 1, fontWeight: 300 }}>{openFaq === i ? '−' : '+'}</span>
                  </div>
                </button>
                {openFaq === i && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} style={{ padding: '0 24px 18px 24px' }}>
                    <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>{faq.a}</p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Section ── */}
      <div className="t-cta-w">
        <motion.div className="t-cta" initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} viewport={{ once: true }}>
          <div>
            <div className="t-cta-tag">Join the Summit</div>
            <h2 className="t-cta-h">Ready to Join Us?</h2>
            <p className="t-cta-p">Register now for the premier academic conference of 2026 and connect with scholars worldwide.</p>
          </div>
          <div className="t-cta-buttons">
            <Link to={`/events${eventId ? `?eventId=${eventId}` : ''}`} className="t-cta-btn">
              Register Now <RiArrowRightLine size={15} />
            </Link>
            <Link to="/about" className="t-cta-btn-outline">
              Learn About Us <RiArrowRightLine size={15} />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}